import { useCallback, useEffect, useState } from 'react';

import { TRAFFICKING_TABLE_NAMES } from '../../../../constants';
import { useFiltersContext } from '../../../../contexts/FilterContext';
import { workWithSessionStorage } from '../../../../utils/storage/sessionStorage';
import { StorageEntity } from '../../../../utils/storage/storageEntity';

// Session-scoped to match the selection it filters, which lives in the `filters` session entity.
export const showSelectedOnlyData = new StorageEntity<boolean>('showSelectedOnly', workWithSessionStorage);

export type UseShowSelectedOnlyResult = {
  /** True only while the toggle is on and something is selected. */
  isShowSelectedOnlyEnabled: boolean;
  /** Whether any row is selected on any tab. */
  hasSelection: boolean;
  setIsShowSelectedOnlyEnabled: (value: boolean) => void;
};

/** Page-level state for the "show selected only" toggle, shared by all tabs and the export. */
const useShowSelectedOnly = (): UseShowSelectedOnlyResult => {
  const { filters } = useFiltersContext();
  // Restoring here is only safe while useFilters hydrates the selection synchronously — otherwise
  // the reset effect below would wipe the restored value before `hasSelection` catches up.
  const [isEnabled, setIsEnabled] = useState(() => showSelectedOnlyData.getAllStorageData() === true);

  const hasSelection = TRAFFICKING_TABLE_NAMES.some(
    (tableName) => (filters[tableName]?.selectedRowIds?.length ?? 0) > 0
  );

  const setIsShowSelectedOnlyEnabled = useCallback((value: boolean): void => {
    showSelectedOnlyData.setAllStorageData(value);
    setIsEnabled(value);
  }, []);

  // Clearing the selection returns the toggle to its default, in storage too.
  useEffect(() => {
    if (!hasSelection) {
      setIsShowSelectedOnlyEnabled(false);
    }
  }, [hasSelection, setIsShowSelectedOnlyEnabled]);

  return {
    // `&& hasSelection` covers the render where the selection empties, before the effect above runs.
    isShowSelectedOnlyEnabled: isEnabled && hasSelection,
    hasSelection,
    setIsShowSelectedOnlyEnabled,
  };
};

export default useShowSelectedOnly;
